import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, TrendingUp, Users, Building2 } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// City data with actual coordinates
const cities = [
  // Northeast
  { name: 'New York', coords: [40.7128, -74.0060], region: 'northeast', drivers: 1250, trips: 45000 },
  { name: 'Boston', coords: [42.3601, -71.0589], region: 'northeast', drivers: 420, trips: 18000 },
  { name: 'Philadelphia', coords: [39.9526, -75.1652], region: 'northeast', drivers: 380, trips: 15000 },
  { name: 'Washington DC', coords: [38.9072, -77.0369], region: 'northeast', drivers: 520, trips: 22000 },
  { name: 'Baltimore', coords: [39.2904, -76.6122], region: 'northeast', drivers: 280, trips: 12000 },
  
  // Southeast
  { name: 'Atlanta', coords: [33.7490, -84.3880], region: 'southeast', drivers: 680, trips: 28000 },
  { name: 'Miami', coords: [25.7617, -80.1918], region: 'southeast', drivers: 540, trips: 24000 },
  { name: 'Orlando', coords: [28.5383, -81.3792], region: 'southeast', drivers: 320, trips: 14000 },
  { name: 'Charlotte', coords: [35.2271, -80.8431], region: 'southeast', drivers: 290, trips: 13000 },
  { name: 'Tampa', coords: [27.9506, -82.4572], region: 'southeast', drivers: 310, trips: 14500 },
  
  // Midwest
  { name: 'Chicago', coords: [41.8781, -87.6298], region: 'midwest', drivers: 920, trips: 38000 },
  { name: 'Detroit', coords: [42.3314, -83.0458], region: 'midwest', drivers: 380, trips: 16000 },
  { name: 'Minneapolis', coords: [44.9778, -93.2650], region: 'midwest', drivers: 290, trips: 12500 },
  { name: 'Cleveland', coords: [41.4993, -81.6944], region: 'midwest', drivers: 260, trips: 11000 },
  { name: 'Columbus', coords: [39.9612, -82.9988], region: 'midwest', drivers: 240, trips: 10500 },
  
  // Southwest
  { name: 'Dallas', coords: [32.7767, -96.7970], region: 'southwest', drivers: 720, trips: 32000 },
  { name: 'Houston', coords: [29.7604, -95.3698], region: 'southwest', drivers: 850, trips: 36000 },
  { name: 'Phoenix', coords: [33.4484, -112.0740], region: 'southwest', drivers: 480, trips: 21000 },
  { name: 'San Antonio', coords: [29.4241, -98.4936], region: 'southwest', drivers: 340, trips: 15000 },
  { name: 'Austin', coords: [30.2672, -97.7431], region: 'southwest', drivers: 380, trips: 17000 },
  
  // West Coast
  { name: 'Los Angeles', coords: [34.0522, -118.2437], region: 'west', drivers: 1100, trips: 48000 },
  { name: 'San Francisco', coords: [37.7749, -122.4194], region: 'west', drivers: 520, trips: 24000 },
  { name: 'San Diego', coords: [32.7157, -117.1611], region: 'west', drivers: 380, trips: 17000 },
  { name: 'Seattle', coords: [47.6062, -122.3321], region: 'west', drivers: 440, trips: 20000 },
  { name: 'Portland', coords: [45.5152, -122.6784], region: 'west', drivers: 280, trips: 13000 },
  
  // Mountain (Expanding)
  { name: 'Denver', coords: [39.7392, -104.9903], region: 'mountain', drivers: 320, trips: 14000, expanding: true },
  { name: 'Las Vegas', coords: [36.1699, -115.1398], region: 'mountain', drivers: 280, trips: 12500, expanding: true },
  { name: 'Salt Lake City', coords: [40.7608, -111.8910], region: 'mountain', drivers: 180, trips: 8000, expanding: true },
  { name: 'Albuquerque', coords: [35.0844, -106.6504], region: 'mountain', drivers: 140, trips: 6000, expanding: true },
];

const regionStats = [
  { id: 'northeast', name: 'Northeast', cities: 45, drivers: 2850, color: '#06b6d4' },
  { id: 'southeast', name: 'Southeast', cities: 38, drivers: 2140, color: '#10b981' },
  { id: 'midwest', name: 'Midwest', cities: 52, drivers: 2090, color: '#3b82f6' },
  { id: 'southwest', name: 'Southwest', cities: 41, drivers: 2770, color: '#f59e0b' },
  { id: 'west', name: 'West Coast', cities: 48, drivers: 2720, color: '#7c3aed' },
  { id: 'mountain', name: 'Mountain', cities: 23, drivers: 920, color: '#ec4899', expanding: true },
];

const CityMarker = ({ city, isSelected, onClick }) => {
  const baseRadius = Math.min(Math.max(city.drivers / 100, 5), 20);
  const color = city.expanding ? '#ec4899' : '#06b6d4';
  
  return (
    <CircleMarker
      center={city.coords}
      radius={isSelected ? baseRadius * 1.5 : baseRadius}
      pathOptions={{
        fillColor: color,
        fillOpacity: isSelected ? 0.9 : 0.7,
        color: 'white',
        weight: isSelected ? 3 : 1,
        opacity: 1,
      }}
      eventHandlers={{
        click: () => onClick(city),
      }}
    >
      <Popup>
        <div className="text-sm p-2">
          <h4 className="font-bold text-lg">{city.name}</h4>
          <div className="mt-2 space-y-1">
            <p className="flex items-center">
              <Users className="w-4 h-4 mr-2 text-cyan-500" />
              <span>{city.drivers.toLocaleString()} drivers</span>
            </p>
            <p className="flex items-center">
              <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
              <span>{city.trips.toLocaleString()} trips/month</span>
            </p>
          </div>
          {city.expanding && (
            <p className="mt-2 text-pink-500 font-medium">Expanding Soon</p>
          )}
        </div>
      </Popup>
    </CircleMarker>
  );
};

// Animated pulse effect for cities
const PulseEffect = ({ coords, color }) => {
  const [scale, setScale] = useState(1);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setScale(prev => prev === 1 ? 1.5 : 1);
    }, 1500);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <CircleMarker
      center={coords}
      radius={30 * scale}
      pathOptions={{
        fillColor: color,
        fillOpacity: 0.1 / scale,
        color: color,
        weight: 1,
        opacity: 0.3 / scale,
      }}
    />
  );
};

export const ExpansionMapSection = () => {
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const filteredCities = selectedRegion 
    ? cities.filter(c => c.region === selectedRegion)
    : cities;

  return (
    <section 
      id="expansion" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="expansion-section"
    >
      <div className="absolute inset-0 neural-bg opacity-20" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            Network Coverage
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Nationwide Coverage
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            12,000+ drivers across 247 cities. Click on cities to see local stats.
          </p>
        </motion.div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4 text-center">
            <MapPin className="w-6 h-6 mx-auto mb-2 text-neon-cyan" />
            <div className="text-3xl font-heading font-bold text-white">247</div>
            <div className="text-sm text-slate-500">Active Cities</div>
          </div>
          <div className="glass-card p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-neon-green" />
            <div className="text-3xl font-heading font-bold text-white">12,847</div>
            <div className="text-sm text-slate-500">Active Drivers</div>
          </div>
          <div className="glass-card p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-neon-purple" />
            <div className="text-3xl font-heading font-bold text-white">+12%</div>
            <div className="text-sm text-slate-500">Monthly Growth</div>
          </div>
          <div className="glass-card p-4 text-center">
            <Building2 className="w-6 h-6 mx-auto mb-2 text-neon-blue" />
            <div className="text-3xl font-heading font-bold text-white">500+</div>
            <div className="text-sm text-slate-500">Healthcare Partners</div>
          </div>
        </div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="glass-card overflow-hidden rounded-2xl"
          data-testid="expansion-map"
        >
          <div className="h-[500px] relative">
            <MapContainer
              center={[39.5, -98.35]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
              scrollWheelZoom={true}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              
              {/* Pulse effects for major cities */}
              {cities.filter(c => c.drivers > 500).map(city => (
                <PulseEffect 
                  key={`pulse-${city.name}`} 
                  coords={city.coords} 
                  color={city.expanding ? '#ec4899' : '#06b6d4'} 
                />
              ))}
              
              {/* City markers */}
              {filteredCities.map(city => (
                <CityMarker
                  key={city.name}
                  city={city}
                  isSelected={selectedCity?.name === city.name}
                  onClick={setSelectedCity}
                />
              ))}
            </MapContainer>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] glass px-4 py-3 rounded-lg">
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-neon-cyan" />
                  <span className="text-slate-300">Active</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-pink-500" />
                  <span className="text-slate-300">Expanding</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-500">
                  <span>Size = Driver count</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Region Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
          {regionStats.map((region) => (
            <motion.button
              key={region.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onClick={() => setSelectedRegion(selectedRegion === region.id ? null : region.id)}
              className={`glass-card p-4 text-center transition-all duration-300 ${
                selectedRegion === region.id ? 'ring-2' : 'hover:border-white/20'
              }`}
              style={{ 
                borderColor: selectedRegion === region.id ? region.color : undefined,
                boxShadow: selectedRegion === region.id ? `0 0 20px ${region.color}40` : undefined
              }}
            >
              <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: region.color }} />
              <h4 className="text-sm font-heading font-semibold text-white mb-1">
                {region.name}
              </h4>
              <p className="text-2xl font-bold" style={{ color: region.color }}>
                {region.cities}
              </p>
              <p className="text-xs text-slate-500">cities</p>
              <p className="text-xs text-slate-600 mt-1">{region.drivers.toLocaleString()} drivers</p>
              {region.expanding && (
                <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-400">
                  EXPANDING
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExpansionMapSection;
